import ImageNotSupportedOutlined from '@mui/icons-material/ImageNotSupportedOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import DeletedTicketImageDialog from '~pages/Settings/DeletedRecords/components/DeletedTicketImageDialog';
import { TICKET_PDF_MIME_TYPE } from '~pages/Settings/DeletedRecords/constants';
import { Nullable } from '~types/Nullable';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';

const THUMBNAIL_SIZE_IN_PX = 50;

interface DeletedTicketImageCellProps {
  imageMimeType: Nullable<string>;
  imageUrl: string;
  previewUrl: string;
  ticketNumber: string | null;
}

// Discarding does not invalidate a signed Active Storage URL, so these still resolve.
export default function DeletedTicketImageCell({
  imageMimeType,
  imageUrl,
  previewUrl,
  ticketNumber,
}: DeletedTicketImageCellProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasFailedToLoad, setHasFailedToLoad] = useState(false);

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);
  const handleImageError = useCallback(() => {
    setHasFailedToLoad(true);
    Sentry.captureException(new Error('FAILED_TO_LOAD_TICKET_IMAGE'), {
      contexts: { ticket_image: { source: 'deleted_records', ticketNumber } },
    });
  }, [ticketNumber]);

  if (hasFailedToLoad) {
    return <MissingImagePlaceholder />;
  }

  const imageAlt = ticketNumber
    ? t('settings.deleted_records.ticket_image_alt', { ticket: ticketNumber })
    : t('settings.deleted_records.ticket_image_alt_unnumbered');
  // previewUrl falls back to the full file, which for a PDF an img cannot render.
  const isPdf = imageMimeType === TICKET_PDF_MIME_TYPE;

  return (
    <>
      <Tooltip title={t('settings.deleted_records.view_ticket_image')}>
        {isPdf ? (
          <Box
            aria-label={imageAlt}
            role="img"
            onClick={openDialog}
            sx={(theme) => ({
              alignItems: 'center',
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              height: `${THUMBNAIL_SIZE_IN_PX}px`,
              justifyContent: 'center',
              minWidth: `${THUMBNAIL_SIZE_IN_PX}px`,
              width: `${THUMBNAIL_SIZE_IN_PX}px`,
            })}
          >
            <PictureAsPdfOutlined sx={{ color: 'text.secondary', fontSize: 22 }} />
          </Box>
        ) : (
          <Box
            component="img"
            alt={imageAlt}
            crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
            loading="lazy"
            src={previewUrl}
            onClick={openDialog}
            onError={handleImageError}
            sx={(theme) => ({
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: 1,
              cursor: 'pointer',
              display: 'block',
              height: `${THUMBNAIL_SIZE_IN_PX}px`,
              minWidth: `${THUMBNAIL_SIZE_IN_PX}px`,
              objectFit: 'cover',
              objectPosition: 'center',
              width: `${THUMBNAIL_SIZE_IN_PX}px`,
            })}
          />
        )}
      </Tooltip>

      <DeletedTicketImageDialog
        imageAlt={imageAlt}
        imageMimeType={imageMimeType}
        imageUrl={imageUrl}
        isOpen={isDialogOpen}
        ticketNumber={ticketNumber}
        onClose={closeDialog}
      />
    </>
  );
}

// Paper and manually-entered tickets have no image.
export function MissingImagePlaceholder() {
  return (
    <Box
      aria-label={t('settings.deleted_records.no_ticket_image')}
      role="img"
      sx={(theme) => ({
        alignItems: 'center',
        border: `dashed 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 1,
        display: 'flex',
        height: `${THUMBNAIL_SIZE_IN_PX}px`,
        justifyContent: 'center',
        minWidth: `${THUMBNAIL_SIZE_IN_PX}px`,
        width: `${THUMBNAIL_SIZE_IN_PX}px`,
      })}
    >
      <ImageNotSupportedOutlined sx={{ color: 'text.disabled', fontSize: 18 }} />
    </Box>
  );
}
